import React from 'react';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import type { AriaProperties } from 'owa-accessibility';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import type { ChevronProps } from 'owa-tree-node/lib/components/TreeNode';
import {
    TimeAndWeatherFilled,
    TimeAndWeatherRegular,
    FlashRegular,
    PersonCircleRegular,
    bundleIcon,
} from '@fluentui/react-icons';
import { isGlobalFeatureEnabled, isAccountFeatureEnabled } from 'owa-feature-flags';
import {
    myOutlookLabel,
    outlookTodayLabel,
    automationsLabel,
    myMirrorLabel,
} from './MyOutlookSection.locstring.json';
import type { MyOutlookSectionProps } from './MyOutlookSection';
import { isMyOutlookFullPageShowing } from 'owa-personal-os-store/lib/selectors/getReadingPaneState';
import { isMyOutlookProductFlightEnabled } from 'owa-personal-os-store/lib/utils/isMyOutlookProductFlightEnabled';

const MY_OUTLOOK_ROOT_NODE_ID = 'myOutlookRoot';
const TodayIcon = bundleIcon(TimeAndWeatherFilled, TimeAndWeatherRegular);

const ariaProps: AriaProperties = {
    role: AriaRoles.group,
    labelledBy: MY_OUTLOOK_ROOT_NODE_ID,
};

const noop = () => {};

const expandedChevronProps: ChevronProps = { isExpanded: true, onClick: noop };

/**
 * Lightweight placeholder rendered while the MyOutlookSection chunk loads.
 * Renders the same stable rows as the full component while its chunk loads.
 *
 * Non-interactive: clicks are no-ops until the real component hydrates.
 */
const MyOutlookSectionPlaceholder: React.MemoExoticComponent<
    React.ForwardRefExoticComponent<
        React.PropsWithoutRef<MyOutlookSectionProps> & React.RefAttributes<HTMLDivElement>
    >
> = observer(
    React.forwardRef<HTMLDivElement, MyOutlookSectionProps>(
        function MyOutlookSectionPlaceholder(props, ref) {
            const { mailboxInfo } = props;
            const showToday =
                isMyOutlookProductFlightEnabled(mailboxInfo) &&
                !isAccountFeatureEnabled('acct-personalos-eligibility', mailboxInfo);
            const showAutomations =
                !showToday && isAccountFeatureEnabled('acct-personalos-automations', mailboxInfo);
            const isProfileV3 =
                !showToday && isAccountFeatureEnabled('acct-personalos-profile-v3', mailboxInfo);
            const hasAdditionalEntries = showAutomations || isProfileV3;
            const isVisible = showToday || hasAdditionalEntries;

            React.useEffect(() => {
                props.onVisibilityChange?.(isVisible);
            }, [props.onVisibilityChange, isVisible]);

            React.useEffect(
                () => () => {
                    props.onVisibilityChange?.(false);
                },
                [props.onVisibilityChange]
            );

            if (!isVisible) {
                return null;
            }

            const useNewSelectionUI = isGlobalFeatureEnabled('fp-newfoldersselection-ui');

            return (
                <div ref={ref} style={props.style} className={props.className}>
                    {showToday && (
                        <TreeNode
                            displayName={loc(outlookTodayLabel)}
                            depth={0}
                            isRootNode={false}
                            isSelected={isMyOutlookFullPageShowing()}
                            customIconComponent={TodayIcon}
                            ellipsesOnHover={!hasAdditionalEntries}
                            shouldShowFocusBorder={true}
                            mailboxInfo={mailboxInfo}
                            useNewSelectionUI={useNewSelectionUI}
                        />
                    )}
                    {hasAdditionalEntries && (
                        <div {...generateDomPropertiesForAria(ariaProps)}>
                            <TreeNode
                                id={MY_OUTLOOK_ROOT_NODE_ID}
                                chevronProps={expandedChevronProps}
                                displayName={loc(myOutlookLabel)}
                                depth={0}
                                isRootNode={true}
                                isSelected={false}
                                key="myOutlookRoot"
                                ellipsesOnHover={true}
                                shouldShowFocusBorder={true}
                                mailboxInfo={mailboxInfo}
                                useNewSelectionUI={useNewSelectionUI}
                            />
                            {showAutomations && (
                                <TreeNode
                                    displayName={loc(automationsLabel)}
                                    depth={0}
                                    isRootNode={false}
                                    isSelected={false}
                                    customIconComponent={FlashRegular}
                                    ellipsesOnHover={false}
                                    shouldShowFocusBorder={true}
                                    mailboxInfo={mailboxInfo}
                                    useNewSelectionUI={useNewSelectionUI}
                                />
                            )}
                            {isProfileV3 && (
                                <TreeNode
                                    displayName={loc(myMirrorLabel)}
                                    depth={0}
                                    isRootNode={false}
                                    isSelected={false}
                                    customIconComponent={PersonCircleRegular}
                                    ellipsesOnHover={false}
                                    shouldShowFocusBorder={true}
                                    mailboxInfo={mailboxInfo}
                                    useNewSelectionUI={useNewSelectionUI}
                                />
                            )}
                        </div>
                    )}
                </div>
            );
        }
    ),
    'MyOutlookSectionPlaceholder'
);

export default MyOutlookSectionPlaceholder;
